import { Extension } from "@tiptap/core";
import { Suggestion, SuggestionOptions } from "@tiptap/suggestion";

import { itemsFn } from "./menu/filter";
import { renderMenu } from "./menu/render";

interface AutocompleteOptions {
  getSuggestions: () => string[];
}

const findSuggestionMatch =
  (getSuggestions: () => string[]): SuggestionOptions["findSuggestionMatch"] =>
  ({ $position }) => {
    // Only show suggestions when cursor is at the end of the text block
    if ($position.pos !== $position.end()) {
      return null;
    }

    const query = $position.doc.textBetween(
      $position.start(),
      $position.pos,
      "\0",
      "\0",
    );

    // No suggestions for empty input or exact matches
    const isExactMatch = getSuggestions().some(
      (s) => s.toLowerCase() === query.toLowerCase(),
    );
    if (!query || isExactMatch) {
      return null;
    }

    return {
      range: { from: $position.start(), to: $position.pos },
      query,
      text: query,
    };
  };

export const AutocompleteExtension = Extension.create<AutocompleteOptions>({
  name: "autocomplete",
  addProseMirrorPlugins() {
    if (this.options.getSuggestions().length === 0) {
      return [];
    }

    return [
      Suggestion({
        editor: this.editor,
        char: "",
        allowSpaces: true,
        startOfLine: true,

        items: itemsFn(this.options.getSuggestions),
        findSuggestionMatch: findSuggestionMatch(this.options.getSuggestions),
        command: ({ editor, range, props }) => {
          if (!("id" in props)) return;

          editor
            .chain()
            .focus()
            .deleteRange(range)
            .insertContentAt(range.from, props.id)
            .run();
        },
        render: () => renderMenu("autocomplete"),
      }),
    ];
  },
});
